import { useState, createContext, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  ArrowRight, 
  Check, 
  Mail, 
  Inbox,
  Send,
  Clock,
  ListTodo,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  Sun,
  Moon,
  Sparkles,
  Shield,
  Lock,
  Globe,
  Zap,
  AlertTriangle,
  Users,
  Building2,
  TrendingUp,
  Target,
  Timer,
  BarChart3,
  CheckCircle2,
  Server,
  FileCheck,
  Eye,
  ShieldCheck
} from "lucide-react";
import { useTheme, useAuth } from "../App";

// Translation data
const translations = {
  fr: {
    nav: {
      features: "Fonctionnalités",
      pricing: "Tarifs",
      security: "Sécurité",
      faq: "FAQ",
      login: "Connexion",
      demo: "Découvrir la démo",
      dashboard: "Tableau de bord",
      logout: "Déconnexion"
    },
    hero: {
      badge: "🪄 Email + Gestion de Projets — tout en un",
      title1: "Reprenez le contrôle",
      title2: "opérationnel de votre",
      title3: "business",
      subtitle: "Que ce soit votre flux email Outlook ou vos projets clients, ALIA centralise, priorise et structure votre activité grâce à l'intelligence artificielle.",
      blockMail: "📧 ALIA Mail — Catégorisez, résumez et répondez à vos emails en quelques secondes",
      blockProjects: "📁 ALIA Projets — Structurez vos projets avec 13 livrables types, phases et tâches",
      cta1: "Découvrir la démo",
      cta2: "Parler à un conseiller",
      billing: "14 jours d'essai gratuit, sans carte bancaire"
    },
    trustBar: {
      text: "Déployé dans les environnements Outlook les plus exigeants"
    },
    problem: {
      title: "Le coût caché de la surcharge email",
      subtitle: "Chaque email non traité est une décision retardée. Chaque relance oubliée est une opportunité perdue.",
      stats: [
        { stat: "28%", label: "du temps de travail", desc: "passé à gérer les emails (McKinsey)" },
        { stat: "12 500€", label: "par collaborateur/an", desc: "coût de la surcharge email" },
        { stat: "23 min", label: "pour se reconcentrer", desc: "après une interruption email" }
      ]
    },
    flagship: {
      badge: "Intelligence Opérationnelle",
      title: "Deux capacités clés. Impact immédiat.",
      subtitle: "ALIA réduit la charge cognitive et accélère la prise de décision sur chaque email.",
      matrix: {
        title: "Matrice de Priorité",
        subtitle: "Classification instantanée de chaque email selon l'urgence et l'importance business.",
        demo: "En 3 secondes, vous voyez exactement quels emails exigent une action immédiate.",
        quadrants: {
          urgentImportant: "Action immédiate",
          notUrgentImportant: "À planifier",
          urgentNotImportant: "À déléguer",
          notUrgentNotImportant: "À archiver"
        }
      },
      actions: {
        title: "Recommandations d'Action",
        subtitle: "Pour chaque email, ALIA propose l'action optimale. Un clic suffit.",
        demo: "ALIA transforme chaque email en décision claire, pas en lecture.",
        buttons: ["Répondre", "Déléguer", "Relancer J+3", "Archiver"]
      }
    },
    preview: {
      title: "Visualisez ALIA en action",
      subtitle: "Un tableau de bord qui transforme votre inbox Outlook en centre de contrôle opérationnel",
      bullets: [
        "Classification automatique par priorité business",
        "Recommandations d'action contextuelles",
        "Exécution en un clic depuis Outlook"
      ],
      cta: "Découvrir la démo"
    },
    features: {
      badge: "Moteur d'Intelligence",
      title: "Une couche d'intelligence unifiée",
      subtitle: "ALIA opère comme une infrastructure invisible qui structure et optimise votre flux email.",
      items: [
        {
          title: "Priorisation Intelligente",
          description: "Classification automatique selon la criticité business. Détection du sentiment, de l'urgence et du contexte relationnel."
        },
        {
          title: "Rédaction Contextuelle",
          description: "Génération de réponses basées sur l'historique de la conversation et le ton approprié à chaque interlocuteur."
        },
        {
          title: "Suivi Automatisé",
          description: "Détection des emails sans réponse. Alertes intelligentes et relances programmées automatiquement."
        },
        {
          title: "Conversion en Actions",
          description: "Transformation des emails en tâches assignables avec deadlines et rappels intégrés."
        }
      ]
    },
    howItWorks: {
      badge: "Déploiement",
      title: "Opérationnel en 4 étapes",
      steps: [
        { number: "01", title: "Audit & Démo", description: "Analyse de vos flux email" },
        { number: "02", title: "Intégration Outlook", description: "Connexion native Microsoft" },
        { number: "03", title: "Configuration", description: "Règles métier sur mesure" },
        { number: "04", title: "Activation", description: "ROI mesurable sous 14 jours" }
      ]
    },
    impact: {
      badge: "Retour sur Investissement",
      title: "Impact mesurable dès la première semaine",
      subtitle: "ALIA génère un ROI quantifiable sur chaque collaborateur équipé.",
      metrics: [
        { value: "2h30", label: "récupérées par jour", desc: "Temps réinvesti en tâches à valeur ajoutée" },
        { value: "12 500€", label: "économisés par an", desc: "Par collaborateur (coût horaire moyen)" },
        { value: "85%", label: "de relances automatisées", desc: "Zéro opportunité manquée" },
        { value: "<5 sec", label: "par email", desc: "Temps de décision moyen avec ALIA" }
      ],
      cta: "Calculer votre ROI"
    },
    pricing: {
      badge: "Tarification",
      title: "Choisissez votre plan",
      subtitle: "14 jours d'essai gratuit sur tous les plans, sans carte bancaire.",
      plans: [
        {
          name: "Starter",
          price: "39€/mois",
          description: "Pour les indépendants",
          features: ["1 compte email", "Catégorisation IA", "Suggestions de réponses", "2 000 actions/mois"]
        },
        {
          name: "Pro",
          price: "99€/mois",
          description: "Pour les équipes",
          features: ["5 comptes email", "Automatisation follow-up", "Création de tâches", "10 000 actions/mois", "Support prioritaire"],
          popular: true
        },
        {
          name: "Business",
          price: "250€/mois",
          description: "Pour les organisations",
          features: ["Comptes illimités", "Automatisation avancée", "Collaboration équipe", "Actions illimitées", "Support dédié", "Accès API"]
        }
      ],
      cta: "Démarrer l'essai gratuit",
      popular: "Recommandé",
      trial: "14 jours d'essai gratuit",
      trust: [
        "ROI moyen : 12 500€/utilisateur/an",
        "Sans engagement",
        "Carte ou IBAN"
      ],
      roiCta: "Calculer votre ROI",
      roiNote: "Retour sur investissement typique : 3 à 6 mois"
    },
    security: {
      badge: "Sécurité Enterprise",
      title: "Vos emails méritent une protection maximale",
      subtitle: "Infrastructure de sécurité de niveau bancaire. Aucun compromis sur la confidentialité de vos données professionnelles.",
      items: [
        { icon: "lock", title: "Chiffrement AES-256", desc: "Vos données sont chiffrées en transit et au repos avec les standards bancaires" },
        { icon: "shield", title: "OAuth 2.0 Sécurisé", desc: "Connexion sans stockage de mot de passe - vos identifiants restent chez vous" },
        { icon: "eye", title: "Zéro accès humain", desc: "Aucun employé ne peut lire vos emails - traitement 100% automatisé par IA" },
        { icon: "server", title: "Hébergement EU / RGPD", desc: "Données stockées en Europe avec conformité RGPD totale" },
        { icon: "file", title: "Audit & Traçabilité", desc: "Logs complets de toutes les actions pour votre conformité interne" },
        { icon: "check", title: "Suppression garantie", desc: "Vos données supprimées définitivement sous 30 jours sur demande" }
      ],
      trust: "Conforme aux exigences des secteurs réglementés : juridique, finance, santé"
    },
    faq: {
      badge: "FAQ",
      title: "Questions fréquentes",
      items: [
        {
          question: "Comment ALIA se connecte à ma boîte Outlook ?",
          answer: "ALIA utilise les API officielles Microsoft avec authentification OAuth sécurisée. Vos identifiants ne sont jamais stockés chez nous."
        },
        {
          question: "Mes emails sont-ils lus par des humains ?",
          answer: "Non. Seuls nos algorithmes IA analysent vos emails. Aucun employé n'a accès à vos données. Tout est chiffré et traité de manière automatisée."
        },
        {
          question: "Comment fonctionne la facturation ?",
          answer: "Nous proposons 3 plans (Starter, Pro, Business) avec un essai gratuit de 14 jours, sans carte bancaire. Annulez à tout moment."
        },
        {
          question: "ALIA envoie-t-il des emails automatiquement ?",
          answer: "Par défaut, non. ALIA suggère des réponses que vous validez. En mode automatique, vous définissez des règles strictes avant tout envoi."
        }
      ]
    },
    finalCta: {
      title: "Prêt à reprendre le contrôle opérationnel de vos emails ?",
      subtitle: "Rejoignez les entreprises qui ont transformé leur gestion email avec ALIA.",
      cta: "Découvrir la démo",
      cta2: "Parler à un conseiller"
    },
    footer: {
      brand: "ALIA",
      links: ["Mentions légales", "Politique de confidentialité", "CGV", "Contact"],
      copyright: "© 2025 ALIA. Tous droits réservés."
    }
  },
  en: {
    nav: {
      features: "Features",
      pricing: "Pricing",
      security: "Security",
      faq: "FAQ",
      login: "Login",
      demo: "Explore demo",
      dashboard: "Dashboard",
      logout: "Log out"
    },
    hero: {
      badge: "🪄 Email + Project Management — all in one",
      title1: "Regain operational",
      title2: "control of your",
      title3: "business",
      subtitle: "Whether it's your Outlook email flow or your client projects, ALIA centralizes, prioritizes and structures your activity thanks to artificial intelligence.",
      blockMail: "📧 ALIA Mail — Categorize, summarize and reply to your emails in seconds",
      blockProjects: "📁 ALIA Projects — Structure your projects with 13 deliverable templates, phases and tasks",
      cta1: "Try the demo",
      cta2: "Talk to an advisor",
      billing: "14-day free trial, no credit card required"
    },
    trustBar: {
      text: "Deployed in the most demanding Outlook environments"
    },
    problem: {
      title: "The hidden cost of email overload",
      subtitle: "Every unprocessed email is a delayed decision. Every missed follow-up is a lost opportunity.",
      stats: [
        { stat: "28%", label: "of work time", desc: "spent managing emails (McKinsey)" },
        { stat: "€12,500", label: "per employee/year", desc: "cost of email overload" },
        { stat: "23 min", label: "to refocus", desc: "after an email interruption" }
      ]
    },
    flagship: {
      badge: "Operational Intelligence",
      title: "Two core capabilities. Immediate impact.",
      subtitle: "ALIA reduces cognitive load and accelerates decision-making on every email.",
      matrix: {
        title: "Priority Matrix",
        subtitle: "Instant classification of every email by urgency and business importance.",
        demo: "In 3 seconds, you see exactly which emails require immediate action.",
        quadrants: {
          urgentImportant: "Immediate action",
          notUrgentImportant: "Schedule",
          urgentNotImportant: "Delegate",
          notUrgentNotImportant: "Archive"
        }
      },
      actions: {
        title: "Action Recommendations",
        subtitle: "For each email, ALIA suggests the optimal action. One click is enough.",
        demo: "ALIA transforms every email into a clear decision, not a reading task.",
        buttons: ["Reply", "Delegate", "Follow-up D+3", "Archive"]
      }
    },
    preview: {
      title: "See ALIA in action",
      subtitle: "A dashboard that transforms your Outlook inbox into an operational control center",
      bullets: [
        "Automatic classification by business priority",
        "Contextual action recommendations",
        "One-click execution from Outlook"
      ],
      cta: "Try the demo"
    },
    features: {
      badge: "Intelligence Engine",
      title: "A unified intelligence layer",
      subtitle: "ALIA operates as an invisible infrastructure that structures and optimizes your email flow.",
      items: [
        {
          title: "Smart Prioritization",
          description: "Automatic classification by business criticality. Detection of sentiment, urgency and relational context."
        },
        {
          title: "Contextual Drafting",
          description: "Response generation based on conversation history and the appropriate tone for each contact."
        },
        {
          title: "Automated Follow-up",
          description: "Detection of unanswered emails. Smart alerts and automatically scheduled follow-ups."
        },
        {
          title: "Action Conversion",
          description: "Transform emails into assignable tasks with integrated deadlines and reminders."
        }
      ]
    },
    howItWorks: {
      badge: "Deployment",
      title: "Operational in 4 steps",
      steps: [
        { number: "01", title: "Audit & Demo", description: "Analysis of your email flows" },
        { number: "02", title: "Outlook Integration", description: "Native Microsoft connection" },
        { number: "03", title: "Configuration", description: "Custom business rules" },
        { number: "04", title: "Activation", description: "Measurable ROI within 14 days" }
      ]
    },
    impact: {
      badge: "Return on Investment",
      title: "Measurable impact from week one",
      subtitle: "ALIA generates quantifiable ROI for every equipped employee.",
      metrics: [
        { value: "2h30", label: "recovered per day", desc: "Time reinvested in high-value tasks" },
        { value: "€12,500", label: "saved per year", desc: "Per employee (average hourly cost)" },
        { value: "85%", label: "automated follow-ups", desc: "Zero missed opportunities" },
        { value: "<5 sec", label: "per email", desc: "Average decision time with ALIA" }
      ],
      cta: "Calculate your ROI"
    },
    pricing: {
      badge: "Pricing",
      title: "Choose your plan",
      subtitle: "14-day free trial on all plans, no credit card required.",
      plans: [
        {
          name: "Starter",
          price: "€39/month",
          description: "For freelancers",
          features: ["1 email account", "AI categorization", "Response suggestions", "2,000 actions/month"]
        },
        {
          name: "Pro",
          price: "€99/month",
          description: "For teams",
          features: ["5 email accounts", "Follow-up automation", "Task creation", "10,000 actions/month", "Priority support"],
          popular: true
        },
        {
          name: "Business",
          price: "€250/month",
          description: "For organizations",
          features: ["Unlimited accounts", "Advanced automation", "Team collaboration", "Unlimited actions", "Dedicated support", "API access"]
        }
      ],
      cta: "Start free trial",
      popular: "Recommended",
      trial: "14-day free trial",
      trust: [
        "Average ROI: €12,500/user/year",
        "No credit card required",
        "Card or IBAN"
      ],
      roiCta: "Calculate your ROI",
      roiNote: "Typical return on investment: 3 to 6 months"
    },
    security: {
      badge: "Enterprise Security",
      title: "Your emails deserve maximum protection",
      subtitle: "Bank-level security infrastructure. No compromise on the confidentiality of your professional data.",
      items: [
        { icon: "lock", title: "AES-256 Encryption", desc: "Your data is encrypted in transit and at rest with banking standards" },
        { icon: "shield", title: "Secure OAuth 2.0", desc: "Connection without password storage - your credentials stay with you" },
        { icon: "eye", title: "Zero Human Access", desc: "No employee can read your emails - 100% automated AI processing" },
        { icon: "server", title: "EU Hosting / GDPR", desc: "Data stored in Europe with full GDPR compliance" },
        { icon: "file", title: "Audit & Traceability", desc: "Complete logs of all actions for your internal compliance" },
        { icon: "check", title: "Guaranteed Deletion", desc: "Your data permanently deleted within 30 days on request" }
      ],
      trust: "Compliant with regulated sector requirements: legal, finance, healthcare"
    },
    faq: {
      badge: "FAQ",
      title: "Frequently asked questions",
      items: [
        {
          question: "How does ALIA connect to my inbox?",
          answer: "ALIA uses official Microsoft Outlook APIs with secure OAuth authentication. Your credentials are never stored with us."
        },
        {
          question: "Are my emails read by humans?",
          answer: "No. Only our AI algorithms analyze your emails. No employee has access to your data. Everything is encrypted and processed automatically."
        },
        {
          question: "How does billing work?",
          answer: "We offer 3 plans (Starter, Pro, Business) with a 14-day free trial, no credit card required. Cancel anytime."
        },
        {
          question: "Does ALIA send emails automatically?",
          answer: "By default, no. ALIA suggests responses that you validate. In automatic mode, you define strict rules before any sending."
        }
      ]
    },
    finalCta: {
      title: "Ready to regain operational control of your emails?",
      subtitle: "Join enterprises that have transformed their email management with ALIA.",
      cta: "Try the demo",
      cta2: "Talk to an advisor"
    },
    footer: {
      brand: "ALIA",
      links: ["Legal Notice", "Privacy Policy", "Terms of Service", "Contact"],
      copyright: "© 2025 ALIA. All rights reserved."
    }
  }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [lang, setLang] = useState("fr");

  const t = translations[lang];
  const featureIcons = [Inbox, Send, Clock, ListTodo];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                {t.nav.features}
              </a>
              <a href="#pricing" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                {t.nav.pricing}
              </a>
              <a href="#security" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                {t.nav.security}
              </a>
              <a href="#faq" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                {t.nav.faq}
              </a>
            </div>

            {/* Right Actions */}
            <div className="hidden md:flex items-center gap-3">
              {/* Language Toggle */}
              <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLang("fr")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "fr" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "en" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  EN
                </button>
              </div>
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-slate-400" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </button>
              {user ? (
                <>
                  <Button
                    onClick={() => navigate("/dashboard/inbox")}
                    className="bg-blue-600 hover:bg-blue-700 text-white glow-blue"
                    data-testid="dashboard-btn"
                  >
                    {t.nav.dashboard}
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-slate-700 dark:text-slate-300"
                    onClick={async () => { await logout(); navigate("/"); }}
                    data-testid="logout-btn"
                  >
                    {t.nav.logout}
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  className="text-slate-700 dark:text-slate-300"
                  onClick={() => navigate("/login")}
                  data-testid="login-btn"
                >
                  {t.nav.login}
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLang("fr")}
                  className={`px-2 py-1 text-xs font-medium ${lang === "fr" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-400"}`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-2 py-1 text-xs font-medium ${lang === "en" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-400"}`}
                >
                  EN
                </button>
              </div>
              <button 
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-700 dark:text-white" />
                ) : (
                  <Menu className="w-6 h-6 text-slate-700 dark:text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block py-2 text-slate-600 dark:text-slate-300">
                {t.nav.features}
              </a>
              <a href="#pricing" className="block py-2 text-slate-600 dark:text-slate-300">
                {t.nav.pricing}
              </a>
              <a href="#security" className="block py-2 text-slate-600 dark:text-slate-300">
                {t.nav.security}
              </a>
              <a href="#faq" className="block py-2 text-slate-600 dark:text-slate-300">
                {t.nav.faq}
              </a>
              <div className="pt-4 flex flex-col gap-2">
                {user ? (
                  <>
                    <Button
                      onClick={() => { setMobileMenuOpen(false); navigate("/dashboard/inbox"); }}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid="dashboard-btn-mobile"
                    >
                      {t.nav.dashboard}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={async () => { setMobileMenuOpen(false); await logout(); navigate("/"); }}
                      data-testid="logout-btn-mobile"
                    >
                      {t.nav.logout}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setMobileMenuOpen(false); navigate("/login"); }}
                    data-testid="login-btn-mobile"
                  >
                    {t.nav.login}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        {/* Background Glow */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20" data-testid="hero-badge">
            <Sparkles className="w-3 h-3 mr-1" />
            {t.hero.badge}
          </Badge>

          <h1 
            className="text-4xl sm:text-5xl md:text-7xl font-bold text-slate-900 dark:text-white tracking-tight mb-6 animate-fade-in"
            style={{ fontFamily: 'Outfit' }}
            data-testid="hero-title"
          >
            {t.hero.title1}
            <br />
            {t.hero.title2} <span className="text-blue-600">{t.hero.title3}</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-8 animate-fade-in stagger-1" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>

          {/* Two modules blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-10 animate-fade-in stagger-1" data-testid="hero-modules">
            <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 backdrop-blur" data-testid="hero-block-mail">
              <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed text-left">
                {t.hero.blockMail}
              </p>
            </div>
            <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 backdrop-blur" data-testid="hero-block-projects">
              <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed text-left">
                {t.hero.blockProjects}
              </p>
            </div>
          </div>

          <div className="flex justify-center animate-fade-in stagger-2">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-10 py-6 glow-blue hover:scale-105 transition-all"
              onClick={() => navigate("/demo-live")}
              data-testid="hero-cta-primary"
            >
              {t.hero.cta1}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">
            {t.hero.billing}
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 bg-blue-600/5 dark:bg-blue-600/10 border-y border-blue-600/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <Building2 className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">{t.trustBar.text}</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" />
                <span className="text-xs text-slate-500">GDPR</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-green-500" />
                <span className="text-xs text-slate-500">AES-256</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-green-500" />
                <span className="text-xs text-slate-500">EU Hosted</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section className="py-20 bg-white dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {t.preview.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t.preview.subtitle}
            </p>
          </div>

          {/* Mock Dashboard Preview */}
          <div className="relative max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-2xl bg-slate-900">
              {/* Mock Browser Header */}
              <div className="bg-slate-800 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="flex-1 ml-4">
                  <div className="bg-slate-700 rounded-md px-3 py-1 text-xs text-slate-400 max-w-xs mx-auto">
                    app.alia-assistant.com/dashboard
                  </div>
                </div>
              </div>
              
              {/* Mock Dashboard Content */}
              <div className="p-6 bg-[#0f172a]">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[
                    { label: lang === "fr" ? "Non lus" : "Unread", value: "12", color: "bg-blue-500/10 text-blue-500" },
                    { label: lang === "fr" ? "Urgent" : "Urgent", value: "3", color: "bg-red-500/10 text-red-500" },
                    { label: lang === "fr" ? "À répondre" : "To Reply", value: "8", color: "bg-orange-500/10 text-orange-500" },
                    { label: lang === "fr" ? "Tâches" : "Tasks", value: "5", color: "bg-green-500/10 text-green-500" }
                  ].map((stat, i) => (
                    <div key={i} className="bg-slate-800/50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-xs text-slate-400">{stat.label}</p>
                    </div>
                  ))}
                </div>
                
                {/* Mock Email List */}
                <div className="space-y-2">
                  {[
                    { from: "Client A", subject: lang === "fr" ? "URGENT: Validation contrat" : "URGENT: Contract validation", tag: "urgent", tagColor: "bg-red-500" },
                    { from: "Sophie M.", subject: lang === "fr" ? "Proposition commerciale" : "Commercial proposal", tag: lang === "fr" ? "à répondre" : "to reply", tagColor: "bg-orange-500" },
                    { from: "Support", subject: lang === "fr" ? "Question technique API" : "API Technical question", tag: lang === "fr" ? "à déléguer" : "to delegate", tagColor: "bg-purple-500" }
                  ].map((email, i) => (
                    <div key={i} className="bg-slate-800/30 rounded-lg p-3 flex items-center gap-3 border-l-2 border-blue-500">
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">{email.from}</p>
                        <p className="text-xs text-slate-400 truncate">{email.subject}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] text-white ${email.tagColor}`}>
                        {email.tag}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating badges */}
            <div className="absolute -right-4 top-1/4 bg-white dark:bg-slate-800 rounded-xl shadow-xl p-3 border border-slate-200 dark:border-slate-700 hidden lg:block">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span className="text-sm font-medium text-slate-900 dark:text-white">AI Active</span>
              </div>
            </div>
            
            <div className="absolute -left-4 bottom-1/4 bg-white dark:bg-slate-800 rounded-xl shadow-xl p-3 border border-slate-200 dark:border-slate-700 hidden lg:block">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <span className="text-sm font-medium text-slate-900 dark:text-white">3 {lang === "fr" ? "urgents" : "urgent"}</span>
              </div>
            </div>
          </div>

          {/* Preview Bullets */}
          <div className="flex flex-wrap justify-center gap-6 mt-12">
            {t.preview.bullets.map((bullet, i) => (
              <div key={i} className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-slate-600 dark:text-slate-300">{bullet}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate("/demo-live")}
            >
              <Mail className="w-5 h-5 mr-2" />
              {t.preview.cta}
            </Button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {t.problem.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t.problem.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.problem.stats.map((item, i) => (
              <Card key={i} className="glass-card text-center p-8 hover:shadow-lg transition-all">
                <p className="text-4xl md:text-5xl font-bold text-blue-600 mb-2" style={{ fontFamily: 'Outfit' }}>{item.stat}</p>
                <p className="text-lg font-medium text-slate-900 dark:text-white mb-1">{item.label}</p>
                <p className="text-slate-500 dark:text-slate-400">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Flagship Features Section - Priority Matrix & Action Recommender */}
      <section id="flagship" className="py-20 bg-white dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 border-emerald-600/20">
              <Zap className="w-3 h-3 mr-1" />
              {t.flagship.badge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {t.flagship.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t.flagship.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Priority Matrix */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 md:p-8 border border-slate-200 dark:border-slate-700" data-testid="priority-matrix">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                    {t.flagship.matrix.title}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.flagship.matrix.subtitle}</p>
                </div>
              </div>

              {/* Interactive 2x2 Matrix */}
              <div className="grid grid-cols-2 gap-2 mb-6">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center hover:bg-red-500/20 transition-colors cursor-pointer">
                  <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-red-500 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">{t.flagship.matrix.quadrants.urgentImportant}</p>
                  <p className="text-xs text-slate-500 mt-1">3 emails</p>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center hover:bg-yellow-500/20 transition-colors cursor-pointer">
                  <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-yellow-500 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">{t.flagship.matrix.quadrants.notUrgentImportant}</p>
                  <p className="text-xs text-slate-500 mt-1">7 emails</p>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center hover:bg-purple-500/20 transition-colors cursor-pointer">
                  <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-purple-500 flex items-center justify-center">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400">{t.flagship.matrix.quadrants.urgentNotImportant}</p>
                  <p className="text-xs text-slate-500 mt-1">5 emails</p>
                </div>
                <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-4 text-center hover:bg-slate-500/20 transition-colors cursor-pointer">
                  <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-slate-500 flex items-center justify-center">
                    <Inbox className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t.flagship.matrix.quadrants.notUrgentNotImportant}</p>
                  <p className="text-xs text-slate-500 mt-1">12 emails</p>
                </div>
              </div>

              <div className="bg-blue-600/10 rounded-lg p-4 border border-blue-600/20">
                <p className="text-sm text-blue-700 dark:text-blue-300 italic">
                  "{t.flagship.matrix.demo}"
                </p>
              </div>
            </div>

            {/* Action Recommender */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 md:p-8 border border-slate-200 dark:border-slate-700" data-testid="action-recommender">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                    {t.flagship.actions.title}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.flagship.actions.subtitle}</p>
                </div>
              </div>

              {/* Mock Email with Action Buttons */}
              <div className="space-y-4 mb-6">
                <div className="bg-white dark:bg-slate-900 rounded-lg p-4 border border-slate-200 dark:border-slate-700 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Marc Dupont</p>
                      <p className="text-sm text-slate-500">{lang === "fr" ? "Proposition commerciale - Projet Q1" : "Sales proposal - Q1 Project"}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs bg-red-500 text-white">{lang === "fr" ? "Urgent" : "Urgent"}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {t.flagship.actions.buttons.map((btn, i) => {
                      const colors = [
                        "bg-blue-600 hover:bg-blue-700 text-white",
                        "bg-purple-600 hover:bg-purple-700 text-white",
                        "bg-yellow-500 hover:bg-yellow-600 text-white",
                        "bg-slate-500 hover:bg-slate-600 text-white"
                      ];
                      const icons = [Send, Users, Clock, Inbox];
                      const Icon = icons[i];
                      return (
                        <button 
                          key={i}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${colors[i]}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {btn}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-emerald-600/10 rounded-lg p-4 border border-emerald-600/20">
                <p className="text-sm text-emerald-700 dark:text-emerald-300 italic">
                  "{t.flagship.actions.demo}"
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate("/demo-live")}
            >
              {t.hero.cta1}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20">
              {t.features.badge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {t.features.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {t.features.items.map((feature, i) => {
              const Icon = featureIcons[i];
              return (
                <Card 
                  key={i} 
                  className="glass-card p-6 md:p-8 hover:shadow-lg hover:border-blue-500/30 transition-all group"
                  data-testid={`feature-card-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center group-hover:bg-blue-600/20 transition-colors">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20">
              {t.howItWorks.badge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
              {t.howItWorks.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.howItWorks.steps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-600/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Outfit' }}>{step.number}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                  {step.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section - NEW */}
      <section id="impact" className="py-20 bg-white dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 border-emerald-600/20">
              <TrendingUp className="w-3 h-3 mr-1" />
              {t.impact.badge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {t.impact.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t.impact.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {t.impact.metrics.map((metric, i) => {
              const icons = [Timer, Target, Zap, BarChart3];
              const Icon = icons[i];
              return (
                <Card 
                  key={i} 
                  className="glass-card p-6 text-center hover:shadow-lg hover:border-emerald-500/30 transition-all group"
                  data-testid={`impact-metric-${i}`}
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                    <Icon className="w-6 h-6 text-emerald-500" />
                  </div>
                  <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400 mb-2" style={{ fontFamily: 'Outfit' }}>
                    {metric.value}
                  </p>
                  <p className="text-lg font-medium text-slate-900 dark:text-white mb-1">
                    {metric.label}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {metric.desc}
                  </p>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => navigate("/roi")}
              data-testid="impact-cta"
            >
              {t.impact.cta}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20">
              {t.pricing.badge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {t.pricing.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              {t.pricing.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {t.pricing.plans.map((plan, i) => {
              const isPopular = plan.popular === true;
              return (
                <Card 
                  key={i} 
                  className={`glass-card p-6 relative transition-all hover:shadow-lg ${isPopular ? 'border-blue-500 shadow-lg shadow-blue-500/20 scale-[1.02]' : ''}`}
                  data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                >
                  {isPopular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4">
                      {t.pricing.popular}
                    </Badge>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                      {plan.name}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
                      {plan.description}
                    </p>
                    <div className="py-2">
                      <span className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                        {plan.price.split('/')[0]}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400">/mois</span>
                    </div>
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                      {t.pricing.trial}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`w-full py-5 ${isPopular ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                    onClick={() => navigate("/register")}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {t.pricing.cta}
                  </Button>
                </Card>
              );
            })}
          </div>

          {/* ROI Focus instead of price trust */}
          <div className="max-w-2xl mx-auto mt-12">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-white">{t.pricing.roiNote}</h4>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                {t.pricing.trust.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section - Enterprise Grade */}
      <section id="security" className="py-20 bg-slate-900 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              <ShieldCheck className="w-3 h-3 mr-1" />
              {t.security.badge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              {t.security.title}
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              {t.security.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {t.security.items.map((item, i) => {
              const iconMap = {
                lock: Lock,
                shield: Shield,
                eye: Eye,
                server: Server,
                file: FileCheck,
                check: CheckCircle2
              };
              const Icon = iconMap[item.icon] || Shield;
              return (
                <div 
                  key={i} 
                  className="p-6 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
                  data-testid={`security-item-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Trust statement for regulated industries */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <Shield className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-300 font-medium text-sm">
                {t.security.trust}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20">
              {t.faq.badge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
              {t.faq.title}
            </h2>
          </div>

          <div className="space-y-4">
            {t.faq.items.map((faq, i) => (
              <Card 
                key={i} 
                className="glass-card overflow-hidden"
                data-testid={`faq-item-${i}`}
              >
                <button
                  className="w-full p-6 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium text-slate-900 dark:text-white pr-4">
                    {faq.question}
                  </span>
                  {openFaq === i ? (
                    <ChevronUp className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-slate-600 dark:text-slate-400">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-32 bg-white dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 dark:text-white mb-6" style={{ fontFamily: 'Outfit' }}>
            {t.finalCta.title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-10">
            {t.finalCta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-10 py-6 glow-blue hover:scale-105 transition-all"
              onClick={() => navigate("/demo-live")}
              data-testid="final-cta"
            >
              {t.finalCta.cta}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600 dark:text-slate-400">
              <Link to="/legal" className="hover:text-blue-600 transition-colors">{t.footer.links[0]}</Link>
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">{t.footer.links[1]}</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">{t.footer.links[2]}</Link>
              <Link to="/contact" className="hover:text-blue-600 transition-colors">{t.footer.links[3]}</Link>
            </div>

            <p className="text-sm text-slate-500 dark:text-slate-500">
              {t.footer.copyright}
            </p>
          </div>
          
          {/* Contact email */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Contact : <a href="mailto:contact@alia-assistant.com" className="text-blue-600 hover:underline">contact@alia-assistant.com</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
